import { useEffect } from 'react'
import exact from 'prop-types-exact'
import { pure } from 'recompose'
import { useHistory, useLocation } from 'react-router-dom'
import login from 'components/auth/login'
import useAuth from 'components/auth/useAuth'
import { getAuthNonce, removeAuthNonce } from 'components/auth/authStorage'
import { isEmpty } from 'lodash'
import { api } from 'api'
import { APPLICATION, makeLoginPath, PATH, QUERY_KEY } from 'config'
import { ERROR_STATUS } from 'types'
import { makeQueryPath, parseQuery } from 'utils'

const propTypes = {}

function PostLogin() {
  const history = useHistory()
  const { search } = useLocation()
  const { code, state } = parseQuery(search)
  const { shouldUseCustomAuth, setIsAuthenticated, setUser } = useAuth()

  useEffect(() => {
    if (!shouldUseCustomAuth) return

    const handleStateValidation = async () => {
      const nonce = getAuthNonce()

      if (isEmpty(nonce)) {
        return login({ appState: { returnTo: PATH.MEMBER_PORTAL } })
      }

      // No matching nonce entry for this state means the login flow can't be
      // validated. Restart it rather than leaving the user on a blank page.
      const authState = nonce[state]

      if (isEmpty(authState)) {
        return login({ appState: { returnTo: PATH.MEMBER_PORTAL } })
      }

      const { code_verifier, returnTo } = authState

      // The exchange both sets the auth cookie and returns the claims in its
      // response body, so we read them straight from here. No follow-up
      // authorization request that would race the freshly set cookie.
      const exchangeResponse = await api.post(makeLoginPath(PATH.CODE, PATH.EXCHANGE), {
        authCode: code,
        authState: state,
        codeVerifier: code_verifier,
      })
      // A hard exchange failure (401/403/404) is already handled by the api
      // layer navigating to the client-error page and resolving to undefined.
      // Bail here so we don't also fire a competing login() restart below.
      if (!exchangeResponse) return

      const claims = exchangeResponse.claims

      // Absent claims (e.g. the API change isn't deployed yet) would otherwise
      // fall through to a half-authenticated user with a null profile. Do not
      // start login again: Auth0 already has a session, so that bounces
      // straight back to /post-login and loops. Stop on the authorization
      // error instead. Tag it as the member portal — this route is /post-login,
      // which the error page would otherwise treat as an unknown app.
      if (isEmpty(claims)) {
        removeAuthNonce()
        window.location.assign(
          makeQueryPath(PATH.CLIENT_ERROR, {
            [QUERY_KEY.APPLICATION]: APPLICATION.MEMBER_PORTAL,
            [QUERY_KEY.STATUS]: ERROR_STATUS.UNAUTHORIZED,
          }),
        )
        return
      }

      removeAuthNonce()
      setIsAuthenticated(true)
      setUser(claims)
      history.push(returnTo)
    }

    handleStateValidation()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseCustomAuth, code, state, setIsAuthenticated, history])

  return null
}

PostLogin.propTypes = exact(propTypes)

export default pure(PostLogin)
